/**
 * Tagestrenner im Verlauf: genau einer direkt vor der ersten Blase jedes lokalen Tages. (pre.3603)
 *
 * ⚠️ 02.10.2026: Nur ändern, was sich ändert. Vorher wurden bei JEDER neuen Blase alle Trenner
 *    gelöscht und neu gesetzt. Der oberste Trenner ist das erste Kind des Verlaufs — mit ihm wechselten
 *    `:first-child` und die Geschwister-Regeln (`* + *`), und der Browser rechnete den Stil des GANZEN
 *    Verlaufs neu. Im Nachbau (300 Nachrichten, Chromium, 4× gedrosselt, tests/browser/chat-viel-inhalt.mts)
 *    waren das rund 2.500 Elemente und bis 290 ms je neuer Blase, mitten im Wischen.
 *
 *    Jetzt bleibt ein Trenner stehen, der schon richtig steht (höchstens die Beschriftung wird nachgezogen:
 *    nach Mitternacht wird „Heute“ zu „Gestern“); fehlende kommen dazu, überzählige gehen. Am selben Tag
 *    ändert eine neue Blase am DOM nichts. Das Ergebnis ist dasselbe wie vorher.
 *
 * ⚠️ Ein Trenner trägt kein `data-ts` — `insertOrdered` (app.ts) ordnet nur nach Blasen mit Stempel, und
 *    die Scroll-Rechnung (lib/scroll-ort.ts, `KEIN_ANKER`) nimmt einen Trenner nie als Anker.
 */

export const TRENNER_KLASSE = "chat-daysep";
export const TRENNER_LABEL_KLASSE = "chat-daysep-label";

interface TrennerArt {
  /** Schlüssel des lokalen Tages (gleich = derselbe Tag). */
  tag: (ts: number) => string;
  /** Beschriftung, z. B. „Heute“, „Gestern“, „Montag, 5. Oktober“. */
  beschriftung: (ts: number) => string;
}

/** Einen Trenner bauen (Klasse und Aufbau wie bisher: div.chat-daysep > span.chat-daysep-label). */
function baueTrenner(doc: Document, text: string): HTMLElement {
  const el = doc.createElement("div");
  el.className = TRENNER_KLASSE;
  const span = doc.createElement("span");
  span.className = TRENNER_LABEL_KLASSE;
  span.textContent = text;
  el.appendChild(span);
  return el;
}

/**
 * Die Trenner im Verlauf in Ordnung bringen — mit so wenigen Änderungen wie möglich.
 *
 * @returns wie viele Knoten eingefügt, entfernt oder umbeschriftet wurden (0 = DOM unberührt).
 */
export function ordneTagestrenner(chat: HTMLElement, art: TrennerArt): number {
  const bleiben = new Set<Element>();
  let aenderungen = 0;
  let letzterTag = "";
  for (const el of Array.from(chat.children) as HTMLElement[]) {
    if (el.classList.contains(TRENNER_KLASSE)) continue;
    const roh = el.dataset?.ts;
    if (!roh) continue; // nur echte Blasen tragen data-ts
    const ts = Number(roh);
    if (!Number.isFinite(ts)) continue;
    const tag = art.tag(ts);
    if (tag === letzterTag) continue;
    letzterTag = tag;
    const davor = el.previousElementSibling as HTMLElement | null;
    if (davor?.classList.contains(TRENNER_KLASSE) && !bleiben.has(davor)) {
      const text = art.beschriftung(ts);
      const span = davor.querySelector(`.${TRENNER_LABEL_KLASSE}`);
      if (span && span.textContent !== text) {
        span.textContent = text;
        aenderungen++;
      }
      bleiben.add(davor);
    } else {
      const neu = baueTrenner(chat.ownerDocument, art.beschriftung(ts));
      chat.insertBefore(neu, el);
      bleiben.add(neu);
      aenderungen++;
    }
  }
  for (const alt of Array.from(chat.children)) {
    if (alt.classList.contains(TRENNER_KLASSE) && !bleiben.has(alt)) {
      alt.remove();
      aenderungen++;
    }
  }
  return aenderungen;
}
